import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import NotificationsPausedOutlinedIcon from '@mui/icons-material/NotificationsPausedOutlined';
import SystemUpdateAltOutlinedIcon from '@mui/icons-material/SystemUpdateAltOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import type { BlogPost } from '@/content/blog';
import { NexusMark } from '@/components/NexusMark';

const covers: Record<BlogPost['cover'], { icon: SvgIconComponent; gradient: string }> = {
  agentic: { icon: AutoFixHighOutlinedIcon, gradient: 'from-[#3a1670] via-[#241040] to-[#0b0b13]' },
  network: { icon: LanOutlinedIcon, gradient: 'from-[#241a4d] via-[#1a1330] to-[#0b0b13]' },
  stitched: { icon: CallSplitOutlinedIcon, gradient: 'from-[#402060] via-[#20122f] to-[#0b0b13]' },
  compliance: { icon: FactCheckOutlinedIcon, gradient: 'from-[#2a1450] via-[#180f2b] to-[#0b0b13]' },
  'buyers-guide': { icon: RuleOutlinedIcon, gradient: 'from-[#331a5c] via-[#1c1133] to-[#0b0b13]' },
  'building-open': { icon: ConstructionOutlinedIcon, gradient: 'from-[#4a2380] via-[#22132f] to-[#0b0b13]' },
  isolation: { icon: SecurityOutlinedIcon, gradient: 'from-[#331a5c] via-[#1c1133] to-[#0b0b13]' },
  'signed-jobs': { icon: VerifiedUserOutlinedIcon, gradient: 'from-[#241a4d] via-[#1a1330] to-[#0b0b13]' },
  'backup-keys': { icon: CloudSyncOutlinedIcon, gradient: 'from-[#2a1450] via-[#180f2b] to-[#0b0b13]' },
  'crm-flow': { icon: SyncAltOutlinedIcon, gradient: 'from-[#402060] via-[#20122f] to-[#0b0b13]' },
  'tenant-hq': { icon: HubOutlinedIcon, gradient: 'from-[#3a1670] via-[#241040] to-[#0b0b13]' },
  'test-ratio': { icon: QueryStatsOutlinedIcon, gradient: 'from-[#4a2380] via-[#22132f] to-[#0b0b13]' },
  autonomy: { icon: GavelOutlinedIcon, gradient: 'from-[#331a5c] via-[#1c1133] to-[#0b0b13]' },
  'design-partner': { icon: Groups2OutlinedIcon, gradient: 'from-[#2a1450] via-[#180f2b] to-[#0b0b13]' },
  itdocs: { icon: ArticleOutlinedIcon, gradient: 'from-[#241a4d] via-[#1a1330] to-[#0b0b13]' },
  execdash: { icon: DashboardOutlinedIcon, gradient: 'from-[#402060] via-[#20122f] to-[#0b0b13]' },
  'solo-team': { icon: PersonOutlineOutlinedIcon, gradient: 'from-[#331a5c] via-[#1c1133] to-[#0b0b13]' },
  'first-hire': { icon: GroupAddOutlinedIcon, gradient: 'from-[#3a1670] via-[#241040] to-[#0b0b13]' },
  'small-team-cost': { icon: SavingsOutlinedIcon, gradient: 'from-[#2a1450] via-[#180f2b] to-[#0b0b13]' },
  'alert-noise': { icon: NotificationsPausedOutlinedIcon, gradient: 'from-[#402060] via-[#20122f] to-[#0b0b13]' },
  'patch-windows': { icon: SystemUpdateAltOutlinedIcon, gradient: 'from-[#241a4d] via-[#1a1330] to-[#0b0b13]' },
  'restore-drills': { icon: RestoreOutlinedIcon, gradient: 'from-[#2a1450] via-[#180f2b] to-[#0b0b13]' },
  'client-onboarding': { icon: ChecklistOutlinedIcon, gradient: 'from-[#331a5c] via-[#1c1133] to-[#0b0b13]' },
  'credential-hygiene': { icon: KeyOutlinedIcon, gradient: 'from-[#3a1670] via-[#241040] to-[#0b0b13]' },
};

/** Abstract gradient + icon cover art — no stock photography, matches the dark Nexus brand ramp. */
export function BlogCover({ cover, size = 'sm' }: { cover: BlogPost['cover']; size?: 'sm' | 'lg' }): JSX.Element {
  const { icon: Icon, gradient } = covers[cover];
  return (
    <div
      className={`relative flex aspect-[16/9] items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br ${gradient}`}
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-30 bg-nexus-grid bg-grid [mask-image:radial-gradient(circle_at_50%_40%,#000,transparent_75%)]"
      />
      <Icon
        className="text-nexus-purple-light/70 transition-transform duration-500 ease-out group-hover:scale-110"
        sx={{ fontSize: size === 'lg' ? 72 : 40 }}
      />
      <div className="absolute bottom-3 right-3 opacity-60">
        <NexusMark size={size === 'lg' ? 22 : 16} />
      </div>
    </div>
  );
}
